import { supabase } from '../lib/supabase'

const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again.'
const ACCESS_DENIED_CODE = 'access_denied'

const ERROR_MESSAGE_MAP = {
  '23503': 'This record is linked to other data and cannot be removed.',
  '23505': 'This record already exists.',
  '42501': 'You do not have permission to perform this action.',
  PGRST116: 'No matching record was found.'
}

/**
 * Format a Supabase error into a user-friendly structured error.
 * @param {import('@supabase/supabase-js').PostgrestError | null} error
 * @param {string} fallbackMessage
 * @returns {{ message: string, code: string, details?: string | null, hint?: string | null } | null}
 */
export const formatSupabaseError = (error, fallbackMessage = DEFAULT_ERROR_MESSAGE) => {
  if (!error) return null

  const message = ERROR_MESSAGE_MAP[error.code] || error.message || fallbackMessage

  return {
    message,
    code: error.code || 'unknown',
    details: error.details || null,
    hint: error.hint || null
  }
}

const isMissingRpc = (error) => {
  if (!error) return false
  return error.code === '42883' || error.message?.toLowerCase().includes('function')
}

export const accessDeniedError = (message = 'Access denied.') => ({
  message,
  code: ACCESS_DENIED_CODE
})

export const accessDeniedResponse = (message = 'Access denied.') => ({
  data: null,
  error: accessDeniedError(message)
})

/**
 * Execute a Supabase query and return structured data and error responses.
 * @template T
 * @param {Promise<{ data: T, error: import('@supabase/supabase-js').PostgrestError | null }>} query
 * @param {{ fallbackMessage?: string, allowEmpty?: boolean }} [options]
 * @returns {Promise<{ data: T | null, error: ReturnType<typeof formatSupabaseError> | { message: string, code: string } | null }>}
 */
export const handleQuery = async (query, { fallbackMessage, allowEmpty = true } = {}) => {
  const { data, error } = await query

  if (error) {
    return {
      data: null,
      error: formatSupabaseError(error, fallbackMessage)
    }
  }

  if (!allowEmpty && (data === null || (Array.isArray(data) && data.length === 0))) {
    return {
      data: null,
      error: {
        message: 'No matching record was found.',
        code: 'not_found'
      }
    }
  }

  return { data, error: null }
}

/**
 * Resolve the current user's role using Supabase RPCs.
 */
export const getCurrentUserRole = async () => {
  const adminCheck = await supabase.rpc('is_admin')
  if (!adminCheck.error && adminCheck.data === true) {
    return { data: 'admin', error: null }
  }
  if (adminCheck.error && !isMissingRpc(adminCheck.error)) {
    return { data: null, error: formatSupabaseError(adminCheck.error, 'Unable to verify admin role.') }
  }

  const agentCheck = await supabase.rpc('is_agent')
  if (!agentCheck.error && agentCheck.data === true) {
    return { data: 'agent', error: null }
  }
  if (agentCheck.error && !isMissingRpc(agentCheck.error)) {
    return { data: null, error: formatSupabaseError(agentCheck.error, 'Unable to verify agent role.') }
  }

  const partnerCheck = await supabase.rpc('is_partner')
  if (!partnerCheck.error && partnerCheck.data === true) {
    return { data: 'partner', error: null }
  }
  if (partnerCheck.error && !isMissingRpc(partnerCheck.error)) {
    return { data: null, error: formatSupabaseError(partnerCheck.error, 'Unable to verify partner role.') }
  }

  const profileCheck = await supabase.rpc('get_my_profile')
  if (!profileCheck.error && profileCheck.data?.role) {
    return { data: profileCheck.data.role, error: null }
  }
  if (profileCheck.error && !isMissingRpc(profileCheck.error)) {
    return { data: null, error: formatSupabaseError(profileCheck.error, 'Unable to load your role.') }
  }

  return {
    data: null,
    error: accessDeniedError('Unable to determine your access level.')
  }
}

/**
 * Resolve role + user id context for role-aware services.
 * @param {{ role?: string, userId?: string }} options
 */
export const resolveRoleContext = async ({ role, userId } = {}) => {
  let resolvedRole = role

  if (!resolvedRole) {
    const { data, error } = await getCurrentUserRole()
    if (error) {
      return { role: null, userId, error }
    }
    resolvedRole = data
  }

  if (resolvedRole && resolvedRole !== 'admin' && !userId) {
    return {
      role: resolvedRole,
      userId,
      error: accessDeniedError('User id is required to scope this request.')
    }
  }

  return { role: resolvedRole, userId, error: null }
}

/**
 * Verify event access using the can_manage_event RPC.
 * @param {string | undefined} eventId
 */
export const ensureCanManageEvent = async (eventId) => {
  if (!eventId) {
    return {
      allowed: false,
      error: accessDeniedError('Event id is required to verify access.')
    }
  }

  const { data, error } = await supabase.rpc('can_manage_event', { p_event_id: eventId })

  if (error) {
    if (isMissingRpc(error)) {
      return { allowed: true, fallback: true }
    }

    return {
      allowed: false,
      error: formatSupabaseError(error, 'Access denied.')
    }
  }

  if (!data) {
    return {
      allowed: false,
      error: accessDeniedError('Access denied. You do not have permission to manage this event.')
    }
  }

  return { allowed: true }
}

/**
 * Apply filters to a Supabase query.
 * @param {import('@supabase/supabase-js').PostgrestFilterBuilder<any, any, any>} query
 * @param {Array<{ column: string, operator: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'like' | 'ilike' | 'in' | 'is', value: any }>} [filters]
 */
export const applyFilters = (query, filters = []) => {
  return filters.reduce((builder, filter) => {
    if (!filter || typeof filter.column !== 'string') return builder

    switch (filter.operator) {
      case 'neq':
        return builder.neq(filter.column, filter.value)
      case 'gt':
        return builder.gt(filter.column, filter.value)
      case 'gte':
        return builder.gte(filter.column, filter.value)
      case 'lt':
        return builder.lt(filter.column, filter.value)
      case 'lte':
        return builder.lte(filter.column, filter.value)
      case 'like':
        return builder.like(filter.column, filter.value)
      case 'ilike':
        return builder.ilike(filter.column, filter.value)
      case 'in':
        return Array.isArray(filter.value)
          ? builder.in(filter.column, filter.value)
          : builder
      case 'is':
        return builder.is(filter.column, filter.value)
      case 'eq':
      default:
        return builder.eq(filter.column, filter.value)
    }
  }, query)
}

/**
 * Apply a text search across multiple columns.
 * @param {import('@supabase/supabase-js').PostgrestFilterBuilder<any, any, any>} query
 * @param {string | undefined} search
 * @param {string[]} columns
 */
export const applySearch = (query, search, columns = []) => {
  if (!search || columns.length === 0) return query

  const queryString = columns
    .map((column) => `${column}.ilike.%${search}%`)
    .join(',')

  return query.or(queryString)
}

/**
 * Apply ordering to a Supabase query.
 * @param {import('@supabase/supabase-js').PostgrestFilterBuilder<any, any, any>} query
 * @param {{ column: string, ascending?: boolean } | undefined} orderBy
 */
export const applyOrdering = (query, orderBy) => {
  if (!orderBy?.column) return query
  return query.order(orderBy.column, { ascending: orderBy.ascending ?? true })
}

/**
 * Apply a range to a Supabase query.
 * @param {import('@supabase/supabase-js').PostgrestFilterBuilder<any, any, any>} query
 * @param {{ from: number, to: number } | undefined} range
 */
export const applyRange = (query, range) => {
  if (!range) return query
  return query.range(range.from, range.to)
}

/**
 * Apply role-aware constraints to a Supabase query.
 * @param {import('@supabase/supabase-js').PostgrestFilterBuilder<any, any, any>} query
 * @param {{
 *   role?: 'student' | 'agent' | 'partner' | 'admin',
 *   userId?: string,
 *   ownershipField?: string,
 *   agentField?: string,
 *   studentField?: string,
 *   eventIds?: string[],
 *   studentEventIds?: string[],
 *   partnerEventIds?: string[],
 *   eventIdField?: string
 * }} options
 */
export const applyRoleConstraints = (query, options = {}) => {
  const {
    role,
    userId,
    ownershipField,
    agentField,
    studentField,
    eventIds,
    studentEventIds,
    partnerEventIds,
    eventIdField
  } = options

  if (!role || !userId || role === 'admin') return query
  const resolvedEventField = eventIdField || 'event_id'

  if (role === 'agent') {
    if (agentField) query = query.eq(agentField, userId)
    else if (ownershipField) query = query.eq(ownershipField, userId)

    if (Array.isArray(eventIds) && eventIds.length > 0) {
      query = query.in(resolvedEventField, eventIds)
    }

    return query
  }

  if (role === 'student') {
    if (Array.isArray(studentEventIds) && studentEventIds.length > 0) {
      return query.in(resolvedEventField, studentEventIds)
    }

    if (studentField) return query.eq(studentField, userId)
    if (ownershipField) return query.eq(ownershipField, userId)
  }

  if (role === 'partner') {
    const scopedEventIds = partnerEventIds || eventIds
    if (Array.isArray(scopedEventIds) && scopedEventIds.length > 0) {
      return query.in(resolvedEventField, scopedEventIds)
    }
  }

  return query
}

/**
 * Apply ownership fields to a payload for non-admin roles.
 * @param {Record<string, any>} payload
 * @param {{
 *   role?: 'student' | 'agent' | 'partner' | 'admin',
 *   userId?: string,
 *   ownershipField?: string,
 *   agentField?: string,
 *   studentField?: string
 * }} options
 */
export const applyOwnershipToPayload = (payload, options = {}) => {
  const { role, userId, ownershipField, agentField, studentField } = options

  if (!userId || !role || role === 'admin') return { ...payload }

  const field = role === 'agent'
    ? agentField || ownershipField
    : studentField || ownershipField

  if (!field) return { ...payload }

  return {
    ...payload,
    [field]: payload[field] ?? userId
  }
}

/**
 * Call a Supabase RPC and return structured results.
 * @template T
 * @param {string} functionName
 * @param {Record<string, any>} [params]
 * @param {string} [fallbackMessage]
 */
export const callRpc = async (functionName, params = {}, fallbackMessage) => {
  return handleQuery(supabase.rpc(functionName, params), {
    fallbackMessage: fallbackMessage || 'Unable to load data.',
    allowEmpty: true
  })
}
